import { createApiClient } from '@goodchat/api-client';
import { useAuthStore } from '@/stores/auth.store';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = createApiClient({
  baseUrl: API_BASE_URL,
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  onTokenRefreshed: (accessToken, refreshToken) => {
    useAuthStore.getState().setTokens(accessToken, refreshToken);
  },
  onUnauthorized: () => {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  },
});

// Auth API helpers (these go through the proxy)
export async function loginApi(email: string, password: string) {
  return apiClient.post<
    | {
        user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null };
        accessToken: string;
        refreshToken: string;
      }
    | {
        twoFactorRequired: true;
        twoFactorToken: string;
        method: string;
      }
  >('/auth/login', { email, password });
}

export async function registerApi(data: {
  email: string;
  password: string;
  fullName: string;
  displayName: string;
  homeCity?: string;
  inviteToken: string;
}) {
  return apiClient.post<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null };
    accessToken: string;
    refreshToken: string;
  }>('/auth/register', data);
}

export async function refreshTokenApi(refreshToken: string) {
  return apiClient.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', { refreshToken });
}

export async function fetchMe() {
  return apiClient.get<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null; isGuest: boolean; messageViewStyle: 'classic' | 'bubble' };
    profileComplete: boolean;
    emails: { id: string; email: string; isPrimary: boolean; verifiedAt: string | null }[];
    orgs: { id: string; name: string; slug: string; color: string | null; icon: string | null; role: string }[];
  }>('/me');
}

export async function updateProfile(data: { displayName?: string; fullName?: string; homeCity?: string; messageViewStyle?: 'classic' | 'bubble' }) {
  return apiClient.patch<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null; messageViewStyle: 'classic' | 'bubble' };
  }>('/me', data);
}

export async function fetchOrgMembers(orgId: string) {
  return apiClient.get<{
    members: {
      userId: string;
      fullName: string;
      displayName: string;
      profilePictureUrl: string | null;
      homeCity: string | null;
      role: string;
      joinedAt: string;
      orgEmail: string;
    }[];
  }>(`/orgs/${orgId}/members`);
}

export async function fetchMemberAvailability(orgId: string) {
  return apiClient.get<{
    availability: Array<{ userId: string; isDnd: boolean; isOutsideWorkingHours: boolean }>;
  }>(`/orgs/${orgId}/members/availability`);
}

export async function fetchOrg(orgId: string) {
  return apiClient.get<{
    org: { id: string; name: string; slug: string; color: string | null; icon: string | null; logoUrl: string | null };
    memberCount: number;
    yourRole: string;
    logoPresignedUrl: string | null;
  }>(`/orgs/${orgId}`);
}

export async function fetchOrgBySlug(slug: string) {
  return apiClient.get<{
    org: { id: string; name: string; slug: string; color: string | null; icon: string | null; logoUrl: string | null };
    memberCount: number;
    yourRole: string;
  }>(`/orgs/by-slug/${slug}`);
}

export async function fetchAreaBySlug(orgId: string, slug: string) {
  return apiClient.get<{
    area: { id: string; orgId: string; name: string; slug: string; type: string };
  }>(`/orgs/${orgId}/areas/by-slug/${slug}`);
}

export async function updateAreaSortOrder(orgId: string, areaIds: string[]) {
  return apiClient.put<{ message: string }>(`/orgs/${orgId}/areas/sort-order`, { areaIds });
}

export async function updateOrg(orgId: string, data: Record<string, unknown>) {
  return apiClient.patch<{ org: Record<string, unknown> }>(`/orgs/${orgId}`, data);
}

export async function presignOrgLogo(orgId: string, fileName: string, fileType: string, fileSize: number) {
  return apiClient.post<{ uploadUrl: string; fileKey: string }>(`/orgs/${orgId}/logo`, { fileName, fileType, fileSize });
}

export async function completeOrgLogo(orgId: string, fileKey: string) {
  return apiClient.post<{ org: Record<string, unknown>; logoUrl: string }>(`/orgs/${orgId}/logo/complete`, { fileKey });
}

export async function removeOrgLogo(orgId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/logo`);
}

// Bulk Invite API helper
export async function bulkInviteMembers(orgId: string, csv: string) {
  return apiClient.post<{
    results: Array<{
      email: string;
      status: 'invited' | 'already_member' | 'added' | 'invalid';
      error?: string;
    }>;
    summary: {
      total: number;
      invited: number;
      added: number;
      skipped: number;
      failed: number;
    };
  }>(`/orgs/${orgId}/members/invite-bulk`, { csv });
}

// Area API helpers
export async function fetchAreas(orgId: string) {
  return apiClient.get<{
    areas: {
      id: string;
      orgId: string;
      name: string;
      slug: string;
      type: string;
      visibility: string;
      isDefault: boolean;
      isDm: boolean;
      icon: string | null;
      iconColor: string | null;
      description: string | null;
      isArchived: boolean;
      createdAt: string;
      isMember: boolean;
      memberCount: number;
      lastMessageAt: string | null;
      sortOrder: number | null;
    }[];
  }>(`/orgs/${orgId}/areas`);
}

export async function createArea(
  orgId: string,
  data: { name: string; type: string; visibility?: string },
) {
  return apiClient.post<{ area: Record<string, unknown> }>(`/orgs/${orgId}/areas`, data);
}

export async function joinArea(orgId: string, areaId: string) {
  return apiClient.post<{ message: string }>(`/orgs/${orgId}/areas/${areaId}/join`);
}

export async function leaveArea(orgId: string, areaId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/areas/${areaId}/leave`);
}

// Message API helpers
export async function fetchMessages(
  orgId: string,
  areaId: string,
  params?: { before?: string; after?: string; limit?: number },
) {
  const searchParams = new URLSearchParams();
  if (params?.before) searchParams.set('before', params.before);
  if (params?.after) searchParams.set('after', params.after);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const qs = searchParams.toString();
  return apiClient.get<{
    data: Array<{
      id: string;
      areaId: string;
      threadId: string | null;
      authorId: string;
      content: string | null;
      messageType: string;
      replyToMessageId: string | null;
      sequenceNumber: number;
      isDeleted: boolean;
      editedAt: string | null;
      createdAt: string;
      author: {
        id: string;
        fullName: string;
        displayName: string;
        profilePictureUrl: string | null;
      };
      replyTo: {
        id: string;
        content: string | null;
        authorDisplayName: string;
        isDeleted: boolean;
      } | null;
      attachments?: Array<{
        id: string;
        fileUrl: string;
        thumbnailUrl: string | null;
        fileType: string;
        fileName: string | null;
        fileSize: number;
        width: number | null;
        height: number | null;
      }>;
    }>;
    hasMore: boolean;
  }>(`/orgs/${orgId}/areas/${areaId}/messages${qs ? `?${qs}` : ''}`);
}

export async function sendMessage(
  orgId: string,
  areaId: string,
  data: { content: string; replyToMessageId?: string; attachmentIds?: string[]; voiceMemoId?: string; messageType?: string; notifyAnywayUserIds?: string[] },
) {
  return apiClient.post<{
    message: {
      id: string;
      areaId: string;
      threadId: string | null;
      authorId: string;
      content: string | null;
      messageType: string;
      replyToMessageId: string | null;
      sequenceNumber: number;
      isDeleted: boolean;
      editedAt: string | null;
      createdAt: string;
      author: {
        id: string;
        fullName: string;
        displayName: string;
        profilePictureUrl: string | null;
      };
      replyTo: {
        id: string;
        content: string | null;
        authorDisplayName: string;
        isDeleted: boolean;
      } | null;
    };
  }>(`/orgs/${orgId}/areas/${areaId}/messages`, data);
}

export async function editMessage(messageId: string, content: string) {
  return apiClient.patch<{ message: Record<string, unknown> }>(`/messages/${messageId}`, {
    content,
  });
}

export async function deleteMessage(messageId: string) {
  return apiClient.delete<{ message: string }>(`/messages/${messageId}`);
}

// DM API helpers
export async function createDm(orgId: string, participantIds: string[]) {
  return apiClient.post<{
    area: {
      id: string;
      orgId: string;
      name: string;
      isDm: boolean;
    };
    created: boolean;
  }>(`/orgs/${orgId}/dms`, { participantIds });
}

// Notification / Unread API helpers
export async function fetchUnreadCounts(orgId: string) {
  return apiClient.get<{ counts: Record<string, number> }>(`/orgs/${orgId}/unread-counts`);
}

/**
 * Mark a channel read. Pass `upToSequence` to mean "caught up to HERE" rather
 * than "caught up to now" — read state is a per-channel watermark, so opening
 * one message from the inbox can only be expressed as a bound. Omit it when the
 * user actually opened the channel.
 */
export async function markAreaRead(orgId: string, areaId: string, upToSequence?: number) {
  return apiClient.post<{ lastReadSequence: number }>(
    `/orgs/${orgId}/areas/${areaId}/read`,
    upToSequence === undefined ? undefined : { upToSequence },
  );
}

export async function fetchAreaNotificationSetting(orgId: string, areaId: string) {
  return apiClient.get<{ level: 'all' | 'mentions' | 'muted'; emailOverride: boolean }>(`/orgs/${orgId}/areas/${areaId}/notification-settings`);
}

export async function updateAreaNotificationSetting(
  orgId: string,
  areaId: string,
  data: { level?: 'all' | 'mentions' | 'muted'; emailOverride?: boolean },
) {
  return apiClient.put<{ level: string; emailOverride: boolean }>(`/orgs/${orgId}/areas/${areaId}/notification-settings`, data);
}

export async function registerPushToken(token: string, platform: string) {
  return apiClient.post<{ message: string }>('/me/push-tokens', { token, platform });
}

export async function removePushToken(token: string) {
  return apiClient.delete<{ message: string }>(`/me/push-tokens/${encodeURIComponent(token)}`);
}

// Thread API helpers
export async function fetchThreads(orgId: string, areaId: string, status?: string) {
  const searchParams = new URLSearchParams();
  if (status) searchParams.set('status', status);
  const qs = searchParams.toString();
  return apiClient.get<{
    threads: Array<{
      id: string;
      areaId: string;
      title: string;
      author: { userId: string; displayName: string; profilePictureUrl: string | null };
      isResolved: boolean;
      createdAt: string;
      updatedAt: string;
      messageCount: number;
      lastMessageAt: string | null;
      unreadCount: number;
    }>;
  }>(`/orgs/${orgId}/areas/${areaId}/threads${qs ? `?${qs}` : ''}`);
}

export async function createThread(
  orgId: string,
  areaId: string,
  data: { title: string; firstMessage: { content: string } },
) {
  return apiClient.post<{
    thread: {
      id: string;
      areaId: string;
      title: string;
      author: { userId: string; displayName: string; profilePictureUrl: string | null };
      isResolved: boolean;
      createdAt: string;
      updatedAt: string;
      messageCount: number;
      lastMessageAt: string | null;
      unreadCount: number;
    };
    message: Record<string, unknown>;
  }>(`/orgs/${orgId}/areas/${areaId}/threads`, data);
}

export async function updateThread(
  threadId: string,
  data: { title?: string; isResolved?: boolean },
) {
  return apiClient.patch<{ thread: Record<string, unknown> }>(`/threads/${threadId}`, data);
}

export async function fetchThreadMessages(
  threadId: string,
  params?: { before?: string; after?: string; limit?: number },
) {
  const searchParams = new URLSearchParams();
  if (params?.before) searchParams.set('before', params.before);
  if (params?.after) searchParams.set('after', params.after);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const qs = searchParams.toString();
  return apiClient.get<{
    data: Array<{
      id: string;
      areaId: string;
      threadId: string | null;
      authorId: string;
      content: string | null;
      messageType: string;
      replyToMessageId: string | null;
      sequenceNumber: number;
      isDeleted: boolean;
      editedAt: string | null;
      createdAt: string;
      author: {
        id: string;
        fullName: string;
        displayName: string;
        profilePictureUrl: string | null;
      };
      replyTo: {
        id: string;
        content: string | null;
        authorDisplayName: string;
        isDeleted: boolean;
      } | null;
    }>;
    hasMore: boolean;
  }>(`/threads/${threadId}/messages${qs ? `?${qs}` : ''}`);
}

export async function sendThreadMessage(
  threadId: string,
  data: { content: string; replyToMessageId?: string; voiceMemoId?: string; messageType?: string },
) {
  return apiClient.post<{
    message: {
      id: string;
      areaId: string;
      threadId: string | null;
      authorId: string;
      content: string | null;
      messageType: string;
      replyToMessageId: string | null;
      sequenceNumber: number;
      isDeleted: boolean;
      editedAt: string | null;
      createdAt: string;
      author: {
        id: string;
        fullName: string;
        displayName: string;
        profilePictureUrl: string | null;
      };
      replyTo: {
        id: string;
        content: string | null;
        authorDisplayName: string;
        isDeleted: boolean;
      } | null;
    };
  }>(`/threads/${threadId}/messages`, data);
}

export async function markThreadRead(threadId: string) {
  return apiClient.post<{ lastReadSequence: number }>(`/threads/${threadId}/read`);
}

// Search API
export async function searchMessages(params: {
  q: string;
  orgId?: string;
  areaId?: string;
  authorId?: string;
  after?: string;
  before?: string;
  type?: string;
  limit?: number;
  cursor?: string;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set('q', params.q);
  if (params.orgId) searchParams.set('orgId', params.orgId);
  if (params.areaId) searchParams.set('areaId', params.areaId);
  if (params.authorId) searchParams.set('authorId', params.authorId);
  if (params.after) searchParams.set('after', params.after);
  if (params.before) searchParams.set('before', params.before);
  if (params.type) searchParams.set('type', params.type);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);
  return apiClient.get<{
    results: Array<{
      message: {
        id: string;
        content: string;
        author: { userId: string; displayName: string; profilePictureUrl: string | null };
        messageType: string;
        createdAt: string;
      };
      area: { id: string; name: string };
      org: { id: string; name: string; color: string | null };
      thread: { id: string; title: string } | null;
      context: { before: string | null; after: string | null };
    }>;
    hasMore: boolean;
    totalEstimate: number;
  }>(`/search?${searchParams.toString()}`);
}

// Media API helpers
export async function presignUpload(data: {
  fileName: string;
  fileType: string;
  fileSize: number;
  context: 'attachment' | 'voice_memo' | 'profile_picture';
}) {
  return apiClient.post<{
    uploadUrl: string;
    fileKey: string;
    attachmentId: string;
  }>('/media/presign', data);
}

export async function completeUpload(
  attachmentId: string,
  data: { fileKey: string; width?: number; height?: number },
) {
  return apiClient.post<{
    attachment: {
      id: string;
      fileUrl: string;
      thumbnailUrl: string | null;
      fileType: string;
      fileName: string | null;
      fileSize: number;
      width: number | null;
      height: number | null;
    };
  }>(`/media/${attachmentId}/complete`, data);
}

// ---- Multipart upload (large files) ----

export async function createMultipartUpload(data: {
  fileName: string;
  fileType: string;
  fileSize: number;
  context: 'attachment' | 'voice_memo' | 'profile_picture';
}) {
  return apiClient.post<{
    attachmentId: string;
    fileKey: string;
    uploadId: string;
    partSize: number;
    partCount: number;
  }>('/media/multipart/create', data);
}

export async function getMultipartPartUrls(data: {
  fileKey: string;
  uploadId: string;
  partNumbers: number[];
}) {
  return apiClient.post<{ urls: { partNumber: number; url: string }[] }>(
    '/media/multipart/urls',
    data,
  );
}

export async function completeMultipartUpload(
  attachmentId: string,
  data: {
    fileKey: string;
    uploadId: string;
    parts: { partNumber: number; etag: string }[];
    width?: number;
    height?: number;
  },
) {
  return apiClient.post<{
    attachment: {
      id: string;
      fileUrl: string;
      thumbnailUrl: string | null;
      fileType: string;
      fileName: string | null;
      fileSize: number;
      width: number | null;
      height: number | null;
    };
  }>(`/media/multipart/${attachmentId}/complete`, data);
}

export async function abortMultipartUpload(data: { fileKey: string; uploadId: string }) {
  return apiClient.post<{ ok: boolean }>('/media/multipart/abort', data);
}

export async function fetchGallery(
  orgId: string,
  areaId: string,
  cursor?: string,
) {
  const searchParams = new URLSearchParams();
  if (cursor) searchParams.set('cursor', cursor);
  const qs = searchParams.toString();
  return apiClient.get<{
    attachments: Array<{
      id: string;
      fileUrl: string;
      thumbnailUrl: string | null;
      fileType: string;
      fileName: string | null;
      fileSize: number;
      width: number | null;
      height: number | null;
      createdAt: string;
    }>;
    hasMore: boolean;
  }>(`/orgs/${orgId}/areas/${areaId}/gallery${qs ? `?${qs}` : ''}`);
}

// Reaction API helpers
export async function addReaction(messageId: string, emoji: string) {
  return apiClient.post<{ reaction: { id?: string; emoji: string; userId: string } }>(
    `/messages/${messageId}/reactions`,
    { emoji },
  );
}

export async function removeReaction(messageId: string, emoji: string) {
  return apiClient.delete<{ message: string }>(
    `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
  );
}

export async function fetchReactions(messageId: string) {
  return apiClient.get<{
    reactions: Array<{
      emoji: string;
      count: number;
      users: { userId: string; displayName: string }[];
      includesMe: boolean;
    }>;
  }>(`/messages/${messageId}/reactions`);
}

// Voice Memo API helpers
export async function presignVoiceMemo(data: {
  fileName: string;
  fileType: string;
  fileSize: number;
  orgId: string;
}) {
  return apiClient.post<{
    uploadUrl: string;
    fileKey: string;
    voiceMemoId: string;
  }>('/voice-memos/presign', data);
}

export async function completeVoiceMemo(
  id: string,
  data: { fileKey: string; durationSeconds: number; waveformData: number[]; orgId: string },
) {
  return apiClient.post<{
    voiceMemo: {
      id: string;
      fileUrl: string;
      durationSeconds: number;
      waveformData: number[] | null;
      expiresAt: string | null;
    };
  }>(`/voice-memos/${id}/complete`, data);
}

// Read Receipt & Acknowledgment API helpers
export async function markMessageRead(messageId: string) {
  return apiClient.post<{ message: string }>(`/messages/${messageId}/read`);
}

export async function markMessagesReadBatch(messageIds: string[]) {
  return apiClient.post<{ message: string }>('/messages/read-batch', { messageIds });
}

export async function acknowledgeMessage(messageId: string) {
  return apiClient.post<{ total: number; acknowledgedCount: number }>(`/messages/${messageId}/acknowledge`);
}

export async function fetchMessageReceipts(messageId: string) {
  return apiClient.get<{
    reads: { userId: string; displayName: string; profilePictureUrl: string | null; readAt: string }[];
    notRead: { userId: string; displayName: string; profilePictureUrl: string | null }[];
    acknowledgments?: { userId: string; displayName: string; acknowledgedAt: string }[];
    notAcknowledged?: { userId: string; displayName: string }[];
  }>(`/messages/${messageId}/receipts`);
}

// Admin API helpers
export async function fetchAdminActivity(orgId: string, days?: number) {
  const searchParams = new URLSearchParams();
  if (days) searchParams.set('days', String(days));
  const qs = searchParams.toString();
  return apiClient.get<{
    members: Array<{
      userId: string;
      displayName: string;
      profilePictureUrl: string | null;
      lastActiveAt: string | null;
      messagesSent: number;
      messagesRead: number;
      messagesTotal: number;
      readRate: number;
      acknowledgementsCompleted: number;
      acknowledgementsPending: number;
    }>;
    summary: { activeMembers: number; inactiveMembers: number; avgReadRate: number };
  }>(`/orgs/${orgId}/admin/activity${qs ? `?${qs}` : ''}`);
}

export async function fetchAdminAcknowledgments(orgId: string, status?: string) {
  const searchParams = new URLSearchParams();
  if (status) searchParams.set('status', status);
  const qs = searchParams.toString();
  return apiClient.get<{
    announcements: Array<{
      message: {
        id: string;
        contentPreview: string;
        area: { id: string; name: string };
        thread?: { id: string; title: string } | null;
        createdAt: string;
        author: { userId: string; displayName: string; profilePictureUrl: string | null };
      };
      total: number;
      acknowledgedCount: number;
      acknowledged: { userId: string; displayName: string; acknowledgedAt: string }[];
      notAcknowledged: { userId: string; displayName: string }[];
    }>;
  }>(`/orgs/${orgId}/admin/acknowledgments${qs ? `?${qs}` : ''}`);
}

export async function fetchAdminAuditLog(orgId: string, params?: { action?: string; actorUserId?: string; limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.action) searchParams.set('action', params.action);
  if (params?.actorUserId) searchParams.set('actorUserId', params.actorUserId);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    entries: Array<{
      id: string;
      orgId: string;
      actorUserId: string;
      actorDisplayName: string;
      action: string;
      targetType: string;
      targetId: string;
      metadata: Record<string, unknown> | null;
      createdAt: string;
    }>;
    hasMore: boolean;
  }>(`/orgs/${orgId}/admin/audit-log${qs ? `?${qs}` : ''}`);
}

// Message context API helper
export async function fetchMessageContext(messageId: string, surrounding = 2) {
  return apiClient.get<{
    message: Record<string, unknown>;
    before: Record<string, unknown>[];
    after: Record<string, unknown>[];
    area: { id: string; name: string; orgId: string; isDm: boolean } | null;
  }>(`/messages/${messageId}/context?surrounding=${surrounding}`);
}

// Inbox API helpers
export async function fetchInbox(params?: { type?: string; limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    items: Array<{
      type: 'unread' | 'acknowledgment' | 'reply_later';
      messageId: string;
      areaId: string;
      areaName: string;
      orgId: string;
      orgName: string;
      threadId?: string | null;
      threadTitle?: string | null;
      authorDisplayName: string;
      authorProfilePictureUrl: string | null;
      contentPreview: string;
      messageType: string;
      createdAt: string;
      acknowledgmentSummary?: { total: number; acknowledged: number } | null;
      replyLater?: { id: string; flaggedAt: string; snoozedUntil: string | null } | null;
    }>;
    hasMore: boolean;
    counts: { unread: number; acknowledgmentsPending: number; replyLater: number };
  }>(`/inbox${qs ? `?${qs}` : ''}`);
}

export async function createReplyLater(messageId: string) {
  return apiClient.post<{ replyLater: { id: string; messageId: string; flaggedAt: string } }>(
    '/reply-later',
    { messageId },
  );
}

export async function listReplyLater(params?: { status?: string; limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    items: Array<{
      id: string;
      messageId: string;
      flaggedAt: string;
      snoozedUntil: string | null;
      resolvedAt: string | null;
      message: {
        id: string;
        content: string | null;
        contentPreview: string;
        messageType: string;
        createdAt: string;
        areaId: string;
        areaName: string;
        orgId: string;
        orgName: string;
        threadId: string | null;
        threadTitle: string | null;
        author: { displayName: string; profilePictureUrl: string | null };
      };
    }>;
    hasMore: boolean;
  }>(`/reply-later${qs ? `?${qs}` : ''}`);
}

export async function updateReplyLater(id: string, data: { snoozedUntil?: string | null }) {
  return apiClient.patch<{ replyLater: Record<string, unknown> }>(`/reply-later/${id}`, data);
}

export async function deleteReplyLater(id: string) {
  return apiClient.delete<void>(`/reply-later/${id}`);
}

export async function resolveReplyLater(id: string) {
  return apiClient.post<{ replyLater: Record<string, unknown> }>(`/reply-later/${id}/resolve`);
}

export async function fetchDms(orgId: string) {
  return apiClient.get<{
    dms: Array<{
      id: string;
      orgId: string;
      name: string;
      isDm: boolean;
      createdAt: string;
      participants: {
        userId: string;
        fullName: string;
        displayName: string;
        profilePictureUrl: string | null;
      }[];
      lastMessage: {
        content: string | null;
        authorId: string;
        createdAt: string;
        isDeleted: boolean;
      } | null;
    }>;
  }>(`/orgs/${orgId}/dms`);
}

// Group API helpers
export async function fetchGroups(orgId: string) {
  return apiClient.get<{
    groups: Array<{
      id: string;
      name: string;
      slug: string;
      createdAt: string;
      memberCount: number;
      members: { userId: string; displayName: string; profilePictureUrl: string | null }[];
      linkedAreas: { areaId: string; areaName: string }[];
    }>;
  }>(`/orgs/${orgId}/groups`);
}

export async function createGroup(orgId: string, data: { name: string; slug: string; memberIds: string[] }) {
  return apiClient.post<{ group: { id: string; name: string; slug: string; createdAt: string } }>(`/orgs/${orgId}/groups`, data);
}

export async function updateGroup(orgId: string, groupId: string, data: { name?: string; addMemberIds?: string[]; removeMemberIds?: string[] }) {
  return apiClient.patch<{ success: boolean }>(`/orgs/${orgId}/groups/${groupId}`, data);
}

export async function deleteGroup(orgId: string, groupId: string) {
  return apiClient.delete<{ success: boolean }>(`/orgs/${orgId}/groups/${groupId}`);
}

export async function fetchGroupMembers(orgId: string, groupId: string) {
  return apiClient.get<{
    members: Array<{
      userId: string;
      displayName: string;
      fullName: string;
      profilePictureUrl: string | null;
      addedAt: string;
    }>;
  }>(`/orgs/${orgId}/groups/${groupId}/members`);
}

export async function linkGroupToArea(orgId: string, groupId: string, areaId: string) {
  return apiClient.post<{ success: boolean }>(`/orgs/${orgId}/groups/${groupId}/areas`, { areaId });
}

export async function unlinkGroupFromArea(orgId: string, groupId: string, areaId: string) {
  return apiClient.delete<{ success: boolean }>(`/orgs/${orgId}/groups/${groupId}/areas/${areaId}`);
}

// Pin API helpers
export async function fetchPins(orgId: string, areaId: string) {
  return apiClient.get<{
    pins: Array<{
      id: string;
      pinnedAt: string;
      pinnedBy: { userId: string; displayName: string };
      message: {
        id: string;
        content: string | null;
        messageType: string;
        createdAt: string;
        author: { userId: string; displayName: string; profilePictureUrl: string | null };
      };
    }>;
  }>(`/orgs/${orgId}/areas/${areaId}/pins`);
}

export async function pinMessage(orgId: string, areaId: string, messageId: string) {
  return apiClient.post<{
    pin: {
      id: string;
      pinnedAt: string;
      pinnedBy: { userId: string; displayName: string };
      message: {
        id: string;
        content: string | null;
        messageType: string;
        createdAt: string;
        author: { userId: string; displayName: string; profilePictureUrl: string | null };
      };
    };
  }>(`/orgs/${orgId}/areas/${areaId}/pins`, { messageId });
}

export async function unpinMessage(orgId: string, areaId: string, pinId: string) {
  return apiClient.delete<{ success: boolean }>(`/orgs/${orgId}/areas/${areaId}/pins/${pinId}`);
}

// Bookmark API helpers
export async function fetchBookmarks(params?: { orgId?: string; limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.orgId) searchParams.set('orgId', params.orgId);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    bookmarks: Array<{
      id: string;
      createdAt: string;
      message: {
        id: string;
        content: string | null;
        messageType: string;
        createdAt: string;
        author: { userId: string; displayName: string; profilePictureUrl: string | null };
      };
      area: { id: string; name: string };
      org: { id: string; name: string; color: string | null };
      thread: { id: string; title: string } | null;
    }>;
    hasMore: boolean;
  }>(`/me/bookmarks${qs ? `?${qs}` : ''}`);
}

export async function bookmarkMessage(messageId: string) {
  return apiClient.post<{ bookmark: { id: string; messageId: string; createdAt: string } }>('/me/bookmarks', { messageId });
}

export async function removeBookmark(bookmarkId: string) {
  return apiClient.delete<{ success: boolean }>(`/me/bookmarks/${bookmarkId}`);
}

// Link Preview API helpers
export async function toggleLinkPreview(messageId: string, hidden: boolean) {
  return apiClient.patch<{ success: boolean }>(`/messages/${messageId}/link-preview`, { hidden });
}

// Status API helpers
export async function setMyStatus(data: { emoji?: string; text: string; expiresAt?: string }) {
  return apiClient.put<{
    status: {
      userId: string;
      emoji: string | null;
      text: string;
      expiresAt: string | null;
      createdAt: string;
    };
  }>('/me/status', data);
}

export async function clearMyStatus() {
  return apiClient.delete<{ message: string }>('/me/status');
}

export async function fetchMyStatus() {
  return apiClient.get<{
    status: {
      userId: string;
      emoji: string | null;
      text: string;
      expiresAt: string | null;
      createdAt: string;
    } | null;
  }>('/me/status');
}

// Notification Settings API helpers
export async function fetchNotificationSettings() {
  return apiClient.get<{
    settings: {
      userId: string;
      dndUntil: string | null;
      workingHoursEnabled: boolean;
      workingHours: Record<string, { start: string; end: string }> | null;
      timezone: string;
      emailFallbackEnabled: boolean;
      digestEnabled: boolean;
      updatedAt: string;
    };
  }>('/me/notification-settings');
}

export async function updateNotificationSettings(data: {
  dndUntil?: string | null;
  workingHoursEnabled?: boolean;
  workingHours?: Record<string, { start: string; end: string }>;
  timezone?: string;
  emailFallbackEnabled?: boolean;
  digestEnabled?: boolean;
}) {
  return apiClient.patch<{
    settings: {
      userId: string;
      dndUntil: string | null;
      workingHoursEnabled: boolean;
      workingHours: Record<string, { start: string; end: string }> | null;
      timezone: string;
      emailFallbackEnabled: boolean;
      digestEnabled: boolean;
      updatedAt: string;
    };
  }>('/me/notification-settings', data);
}

// Scheduled Messages API helpers
export async function createScheduledMessage(data: {
  areaId: string;
  threadId?: string;
  content: string;
  messageType?: string;
  scheduledFor: string;
}) {
  return apiClient.post<{ scheduledMessage: Record<string, unknown> }>('/scheduled-messages', data);
}

export async function fetchScheduledMessages(params?: { areaId?: string; limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.areaId) searchParams.set('areaId', params.areaId);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    scheduledMessages: Array<{
      id: string;
      areaId: string;
      threadId: string | null;
      content: string | null;
      messageType: string;
      scheduledFor: string;
      createdAt: string;
      sentAt: string | null;
      cancelledAt: string | null;
      area: { id: string; name: string };
      thread: { id: string; title: string } | null;
    }>;
    hasMore: boolean;
  }>(`/scheduled-messages${qs ? `?${qs}` : ''}`);
}

export async function updateScheduledMessage(id: string, data: { content?: string; scheduledFor?: string }) {
  return apiClient.patch<{ scheduledMessage: Record<string, unknown> }>(`/scheduled-messages/${id}`, data);
}

export async function cancelScheduledMessage(id: string) {
  return apiClient.delete<{ message: string }>(`/scheduled-messages/${id}`);
}

// External Access API helpers
export async function inviteExternal(orgId: string, areaId: string, invites: { email: string; name?: string }[]) {
  return apiClient.post<{
    results: { email: string; status: 'invited' | 'already_member' }[];
  }>(`/orgs/${orgId}/areas/${areaId}/invite-external`, { invites });
}

export async function verifyExternalMagicLink(data: { token: string; fullName: string; displayName?: string }) {
  return apiClient.post<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null; isGuest: boolean };
    accessToken: string;
    refreshToken: string;
    isExternal: boolean;
    areaId: string;
    orgId: string;
  }>('/auth/magic-link/verify-external', data);
}

export async function upgradeAccount(data: { password: string; fullName?: string }) {
  return apiClient.post<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null; isGuest: boolean };
  }>('/me/upgrade', data);
}

// 2FA API helpers
export async function fetch2faStatus() {
  return apiClient.get<{ enabled: boolean; method: string | null; enabledAt: string | null }>('/me/2fa/status');
}

export type TwoFactorSetupResult =
  | { method: 'totp'; secret: string; qrCodeDataUrl: string; otpauthUrl: string }
  | { method: 'sms'; phoneNumber: string; message: string };

export async function setup2fa(method: string, phoneNumber?: string) {
  return apiClient.post<TwoFactorSetupResult>('/me/2fa/setup', { method, phoneNumber });
}

export async function confirm2fa(code: string, options?: { secret?: string; phoneNumber?: string }) {
  return apiClient.post<{ enabled: boolean; recoveryCodes: string[] }>('/me/2fa/confirm', {
    code,
    secret: options?.secret,
    phoneNumber: options?.phoneNumber,
  });
}

export async function disable2fa(password: string) {
  return apiClient.post<{ enabled: boolean }>('/me/2fa/disable', { password });
}

export async function regenerateRecoveryCodes(password: string) {
  return apiClient.post<{ codes: string[] }>('/me/2fa/recovery-codes/regenerate', { password });
}

export async function verify2faLogin(twoFactorToken: string, code: string) {
  return apiClient.post<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null };
    accessToken: string;
    refreshToken: string;
  }>('/auth/2fa/verify', { twoFactorToken, code });
}

export async function recover2faLogin(twoFactorToken: string, recoveryCode: string) {
  return apiClient.post<{
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null };
    accessToken: string;
    refreshToken: string;
  }>('/auth/2fa/recover', { twoFactorToken, recoveryCode });
}

// Session API helpers
export async function fetchSessions() {
  return apiClient.get<{
    sessions: Array<{
      id: string;
      deviceInfo: Record<string, unknown> | null;
      isCurrent: boolean;
      expiresAt: string;
      createdAt: string;
    }>;
  }>('/me/sessions');
}

export async function revokeSession(sessionId: string) {
  return apiClient.delete<{ message: string }>(`/me/sessions/${sessionId}`);
}

export async function revokeAllSessions() {
  return apiClient.delete<{ message: string }>('/me/sessions');
}

// Mention suggestions API helper
export async function fetchMentionSuggestions(orgId: string, q: string) {
  return apiClient.get<{
    suggestions: Array<{
      type: 'user' | 'group' | 'builtin';
      id: string;
      displayName: string;
      slug: string;
      avatarUrl?: string | null;
    }>;
  }>(`/orgs/${orgId}/mention-suggestions?q=${encodeURIComponent(q)}`);
}

// Analytics API helper
export async function fetchAnalytics(orgId: string, days: number = 30) {
  return apiClient.get<{
    messagesPerDay: { date: string; count: number }[];
    activeUsersPerDay: { date: string; count: number }[];
    peakHours: { hour: number; count: number }[];
    topAreas: { areaId: string; areaName: string; count: number }[];
  }>(`/orgs/${orgId}/admin/analytics?days=${days}`);
}

// Login Analytics API helper
export async function fetchLoginAnalytics(orgId: string, days: number = 30) {
  return apiClient.get<{
    failedLoginsPerDay: { date: string; count: number }[];
    totalFailed: number;
    totalSuccess: number;
    topTwoFaFailures: { email: string; count: number }[];
    lockedAccounts: number;
  }>(`/orgs/${orgId}/admin/login-analytics?days=${days}`);
}

// Profile Picture API helpers
export async function presignProfilePicture(fileName: string, fileType: string, fileSize: number) {
  return apiClient.post<{ uploadUrl: string; fileKey: string }>('/me/profile-picture', {
    fileName,
    fileType,
    fileSize,
  });
}

export async function completeProfilePicture(fileKey: string) {
  return apiClient.post<{ profilePictureUrl: string }>('/me/profile-picture/complete', { fileKey });
}

export async function removeProfilePicture() {
  return apiClient.delete<{ message: string }>('/me/profile-picture');
}

export async function fetchSecurityLog(params?: { limit?: number; before?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    entries: Array<{
      id: string;
      action: string;
      targetType: string;
      targetId: string;
      metadata: Record<string, unknown> | null;
      createdAt: string;
    }>;
    hasMore: boolean;
  }>(`/me/security-log${qs ? `?${qs}` : ''}`);
}

// DND Check API helper
export async function checkDndStatus(userIds: string[]) {
  return apiClient.post<{
    users: Array<{
      userId: string;
      displayName: string;
      isDnd: boolean;
      dndUntil: string | null;
      isOutsideWorkingHours: boolean;
    }>;
  }>('/users/dnd-check', { userIds });
}

// Org Sort Order API helper
export async function updateOrgSortOrder(orgIds: string[]) {
  return apiClient.put<{ message: string }>('/orgs/sort-order', { orgIds });
}

// Org Bookmarks API helpers
export async function fetchOrgBookmarks(orgId: string) {
  return apiClient.get<{
    bookmarks: Array<{
      id: string;
      orgId: string;
      title: string;
      url: string;
      description: string | null;
      sortOrder: number;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }>(`/orgs/${orgId}/bookmarks`);
}

export async function createOrgBookmark(orgId: string, data: { title: string; url: string; description?: string }) {
  return apiClient.post<{ bookmark: Record<string, unknown> }>(`/orgs/${orgId}/bookmarks`, data);
}

export async function updateOrgBookmark(orgId: string, bookmarkId: string, data: { title?: string; url?: string; description?: string | null }) {
  return apiClient.patch<{ bookmark: Record<string, unknown> }>(`/orgs/${orgId}/bookmarks/${bookmarkId}`, data);
}

export async function deleteOrgBookmark(orgId: string, bookmarkId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/bookmarks/${bookmarkId}`);
}

export async function updateOrgBookmarkSortOrder(orgId: string, bookmarkIds: string[]) {
  return apiClient.put<{ message: string }>(`/orgs/${orgId}/bookmarks/sort-order`, { bookmarkIds });
}

// Profile Field Config API helpers
export async function fetchProfileFieldConfig(orgId: string) {
  return apiClient.get<{ config: { required: string[]; optional: string[] } }>(`/orgs/${orgId}/profile-fields`);
}

export async function updateProfileFieldConfig(orgId: string, config: { required: string[]; optional: string[] }) {
  return apiClient.patch<{ config: { required: string[]; optional: string[] } }>(`/orgs/${orgId}/profile-fields`, config);
}

// Member Role & Removal API helpers
export async function updateMemberRole(orgId: string, memberId: string, role: string) {
  return apiClient.patch<{ message: string }>(`/orgs/${orgId}/members/${memberId}`, { role });
}

export async function removeMember(orgId: string, memberId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/members/${memberId}`);
}

// Member Deactivation API helpers
export async function deactivateMember(orgId: string, memberId: string) {
  return apiClient.post<{ message: string }>(`/orgs/${orgId}/members/${memberId}/deactivate`);
}

export async function reactivateMember(orgId: string, memberId: string) {
  return apiClient.post<{ message: string }>(`/orgs/${orgId}/members/${memberId}/reactivate`);
}

// Announcement API helper
export async function createAnnouncement(orgId: string, data: { title: string; content: string; areaIds: string[]; requiresAcknowledgment: boolean }) {
  return apiClient.post<{ results: { areaId: string; threadId: string; messageId: string; refMessageId: string }[]; count: number }>(`/orgs/${orgId}/announcements`, data);
}

// Area management helpers
export async function updateArea(orgId: string, areaId: string, data: Record<string, unknown>) {
  return apiClient.patch<{ area: Record<string, unknown> }>(`/orgs/${orgId}/areas/${areaId}`, data);
}

export async function fetchAreaMembers(orgId: string, areaId: string) {
  return apiClient.get<{ members: Array<{ userId: string; fullName: string; displayName: string; profilePictureUrl: string | null; role: string; isExternal: boolean; joinedAt: string }> }>(`/orgs/${orgId}/areas/${areaId}/members`);
}

export async function addAreaMembers(orgId: string, areaId: string, userIds: string[]) {
  return apiClient.post<{ added: string[] }>(`/orgs/${orgId}/areas/${areaId}/members`, { internalUserIds: userIds });
}

export async function removeAreaMember(orgId: string, areaId: string, userId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/areas/${areaId}/members/${userId}`);
}

// ============================================================
// Drafts
// ============================================================

export async function upsertDraft(data: {
  areaId: string;
  threadId?: string | null;
  draftType: string;
  content: Record<string, unknown>;
}) {
  return apiClient.put<{ draft: Record<string, unknown> }>('/drafts', data);
}

export async function fetchDrafts(params?: {
  orgId?: string;
  draftType?: string;
  limit?: number;
  before?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.orgId) searchParams.set('orgId', params.orgId);
  if (params?.draftType) searchParams.set('draftType', params.draftType);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.before) searchParams.set('before', params.before);
  const qs = searchParams.toString();
  return apiClient.get<{
    drafts: Array<{
      id: string;
      areaId: string;
      threadId: string | null;
      draftType: string;
      content: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
      area: { id: string; name: string; slug: string; icon: string | null; iconColor: string | null };
      org: { id: string; name: string; slug: string; color: string | null };
      thread: { id: string; title: string } | null;
    }>;
    hasMore: boolean;
  }>(`/drafts${qs ? `?${qs}` : ''}`);
}

export async function fetchDraftCount() {
  return apiClient.get<{ count: number }>('/drafts/count');
}

export async function lookupDraft(areaId: string, draftType: string, threadId?: string) {
  const searchParams = new URLSearchParams();
  searchParams.set('areaId', areaId);
  searchParams.set('draftType', draftType);
  if (threadId) searchParams.set('threadId', threadId);
  return apiClient.get<{
    draft: {
      id: string;
      areaId: string;
      threadId: string | null;
      draftType: string;
      content: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    } | null;
  }>(`/drafts/lookup?${searchParams.toString()}`);
}

export async function fetchDraftAreaIds() {
  return apiClient.get<{ areaIds: string[] }>('/drafts/area-ids');
}

export async function deleteDraft(id: string) {
  return apiClient.delete<void>(`/drafts/${id}`);
}

// Polls
export async function votePoll(messageId: string, optionId: string) {
  return apiClient.post<{ poll: import('@goodchat/shared/types').Poll }>(`/messages/${messageId}/poll/vote`, { optionId });
}

export async function removeVote(messageId: string, optionId: string) {
  return apiClient.delete<{ poll: import('@goodchat/shared/types').Poll }>(`/messages/${messageId}/poll/vote/${optionId}`);
}

export async function closePoll(messageId: string) {
  return apiClient.patch<{ poll: import('@goodchat/shared/types').Poll }>(`/messages/${messageId}/poll`, {});
}

// Custom Emoji
export async function fetchOrgEmoji(orgId: string) {
  return apiClient.get<{ emoji: import('@goodchat/shared/types').CustomEmoji[] }>(`/orgs/${orgId}/emoji`);
}

export async function createCustomEmoji(orgId: string, data: { name: string; fileName: string; fileType: string; fileSize: number }) {
  return apiClient.post<{ emoji: import('@goodchat/shared/types').CustomEmoji; uploadUrl: string; s3Key: string }>(`/orgs/${orgId}/emoji`, data);
}

export async function completeCustomEmoji(orgId: string, emojiId: string) {
  return apiClient.post<{ emoji: import('@goodchat/shared/types').CustomEmoji }>(`/orgs/${orgId}/emoji/${emojiId}/complete`);
}

export async function deleteCustomEmoji(orgId: string, emojiId: string) {
  return apiClient.delete<{ message: string }>(`/orgs/${orgId}/emoji/${emojiId}`);
}

// Audit Log Export
export function getAuditLogExportUrl(orgId: string, format: 'csv' | 'json', days: number) {
  return `${API_BASE_URL}/orgs/${orgId}/admin/audit-log/export?format=${format}&days=${days}`;
}

// ============================================================
// Comments (design v2) — replies anchored to a message
// ============================================================

export interface CommentSummary {
  threadId: string;
  isInternal: boolean;
  count: number;
  unreadCount: number;
  lastAt: string;
}

export interface Comment {
  id: string;
  threadId: string | null;
  isInternal: boolean;
  content: string | null;
  createdAt: string;
  editedAt: string | null;
  author: { userId: string; displayName: string; profilePictureUrl: string | null };
}

export function fetchComments(messageId: string, opts?: { limit?: number; before?: string }) {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.before) params.set('before', opts.before);
  const qs = params.toString();
  return apiClient.get<{ comments: Comment[]; hasMore: boolean }>(
    `/messages/${messageId}/comments${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Post a comment. `isInternal` defaults to true server-side, but it is passed
 * explicitly here so the caller can never be ambiguous about the audience —
 * the composer always knows which one it is showing the user.
 */
export function createComment(messageId: string, content: string, isInternal: boolean) {
  return apiClient.post<{ comment: Comment; threadId: string; isInternal: boolean }>(
    `/messages/${messageId}/comments`,
    { content, isInternal },
  );
}

// ============================================================
// Todos — pending acks, reply-later, open polls, ad-hoc tasks
// ============================================================
//
// Contract source: apps/api/src/routes/todos.ts. Only `task` is written from
// here. The other three types are VIEWS over data other endpoints own, and
// completing them runs through those endpoints — `acknowledgeMessage`,
// `votePoll`, `resolveReplyLater` — never through a todos endpoint. There is
// deliberately no "complete a todo" call.

type TodosResponsePayload = import('@goodchat/shared/types').TodosResponse;
type TodoTaskPayload = import('@goodchat/shared/types').TodoTask;
type TodoTypeFilter = import('@goodchat/shared/constants').TodoType;
type TodoStatusQuery = import('@goodchat/shared/constants').TodoStatusFilter;

export interface FetchTodosParams {
  /** Omit for the cross-org focused view. */
  orgId?: string;
  /** Set for the contextual pane inside one Chat/Room. */
  areaId?: string;
  /** Omit for every type. Serialized as the comma list the server parses. */
  type?: readonly TodoTypeFilter[];
  /** Server default is 'open'. */
  status?: TodoStatusQuery;
  limit?: number;
  /** Opaque keyset cursor from a previous `nextCursor` — NOT a message id. */
  cursor?: string;
}

/** Oldest-first: what you have ignored longest comes back at the top. */
export async function fetchTodos(params: FetchTodosParams = {}) {
  const searchParams = new URLSearchParams();
  if (params.orgId) searchParams.set('orgId', params.orgId);
  if (params.areaId) searchParams.set('areaId', params.areaId);
  if (params.type && params.type.length > 0) searchParams.set('type', params.type.join(','));
  if (params.status) searchParams.set('status', params.status);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  return apiClient.get<TodosResponsePayload>(`/todos${qs ? `?${qs}` : ''}`);
}

export interface CreateTaskInput {
  orgId: string;
  title: string;
  notes?: string;
  /** Omit for an org-level task — those show only in the focused Todos view. */
  areaId?: string;
  /** "Turn this message into a task" — the server derives the area from it. */
  messageId?: string;
  /** Omit for a room task anyone in `areaId` can complete. */
  assigneeId?: string;
  dueAt?: string;
  /** Defaults to true server-side; forced false when the caller is a guest. */
  isInternal?: boolean;
}

export async function createTask(data: CreateTaskInput) {
  return apiClient.post<{ task: TodoTaskPayload | null }>('/todos/tasks', data);
}

/**
 * Explicit null CLEARS the field; an omitted key leaves it alone. The server
 * distinguishes the two, so never spread a whole task object in here — that
 * would rewrite every field and turn a title edit into a silent unassign.
 * At least one key is required or the request is rejected.
 */
export interface UpdateTaskInput {
  title?: string;
  notes?: string | null;
  dueAt?: string | null;
  assigneeId?: string | null;
  areaId?: string | null;
  isInternal?: boolean;
}

export async function updateTask(taskId: string, data: UpdateTaskInput) {
  return apiClient.patch<{ task: TodoTaskPayload | null }>(`/todos/tasks/${taskId}`, data);
}

/** A room task (no assignee) is done when anyone in the room does it. */
export async function completeTask(taskId: string) {
  return apiClient.post<{ task: TodoTaskPayload | null }>(`/todos/tasks/${taskId}/complete`);
}

export async function reopenTask(taskId: string) {
  return apiClient.post<{ task: TodoTaskPayload | null }>(`/todos/tasks/${taskId}/reopen`);
}

/** 204, no body — creator or org admin only (narrower than edit). */
export async function deleteTask(taskId: string) {
  return apiClient.delete<void>(`/todos/tasks/${taskId}`);
}

/**
 * Pin / unpin a room for yourself (migration 0023). Per-user; idempotent.
 */
export async function pinArea(orgId: string, areaId: string) {
  return apiClient.post<{ pinnedAt: string | null }>(`/orgs/${orgId}/areas/${areaId}/pin`);
}

export async function unpinArea(orgId: string, areaId: string) {
  return apiClient.delete<{ pinnedAt: string | null }>(`/orgs/${orgId}/areas/${areaId}/pin`);
}
